import React from 'react';
import Slider, { Settings, SwipeDirection } from 'react-slick';

import { CircleButton } from 'ui/component';
import { Breakpoint } from 'ui/style';
import { StyleUtil } from 'ui/util';
import { SLIDE_SPEED_IN_MS, CARD_ANIMATION_SPEED_IN_MS } from '../../Constants';
import {
  MachineLearningContentCard,
  MachineReasoningContentCard,
  ProcessAutomationContentCard,
  ScriptingContentCard,
  ZeroTouchContentCard,
} from '../';
import { useStateContext } from '../../context';
import classNames from './EABDContentCardSlider.module.scss';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

export type ContentCardType = 'scripting' | 'process_automation' | 'machine_learning' | 'machine_reasoning' | 'zero_touch' | 'empty';

export interface ContentCardSliderProps {
  readonly contentCards: ContentCardType[];
  readonly currentIndex: number;
  readonly initialPageIndex: number;
  readonly isMobile: boolean;
  readonly afterScroll: (index: number) => void;
}

export const ContentCardSlider = React.memo<ContentCardSliderProps>((props) => {
  const { shouldAutoscrollJourneyAtFirst, setShouldAutoscrollJourneyAtFirst } = useStateContext();
  const autoscrollIntervalRef = React.useRef<NodeJS.Timeout | undefined>(undefined);
  const sliderRef = React.useRef<Slider>();

  const { contentCards, currentIndex, initialPageIndex, isMobile, afterScroll } = props;

  const OnSwipeCallback = React.useCallback(
    (_swipeDirection: SwipeDirection) => {
      if (autoscrollIntervalRef.current) {
        setShouldAutoscrollJourneyAtFirst(false);
        clearInterval(autoscrollIntervalRef.current);
        autoscrollIntervalRef.current = undefined;
      }
    },
    [setShouldAutoscrollJourneyAtFirst]
  );

  const OnPrevPressCallback = React.useCallback(
    (isAutoscroll: boolean = false) => {
      if (!isAutoscroll && autoscrollIntervalRef.current) {
        setShouldAutoscrollJourneyAtFirst(false);
        clearInterval(autoscrollIntervalRef.current);
        autoscrollIntervalRef.current = undefined;
      }

      sliderRef.current?.slickPrev();
    },
    [setShouldAutoscrollJourneyAtFirst]
  );

  const OnNextPressCallback = React.useCallback(
    (isAutoscroll: boolean = false) => {
      if (!isAutoscroll && autoscrollIntervalRef.current) {
        setShouldAutoscrollJourneyAtFirst(false);
        clearInterval(autoscrollIntervalRef.current);
        autoscrollIntervalRef.current = undefined;
      }

      sliderRef.current?.slickNext();
    },
    [setShouldAutoscrollJourneyAtFirst]
  );

  /** Effect for auto scrolling automatically at first **/
  React.useEffect(() => {
    if (!shouldAutoscrollJourneyAtFirst) {
      return;
    }

    let count = 0;
    const handler = () => {
      OnNextPressCallback(true);

      count += 1;

      if (count === 2 && autoscrollIntervalRef.current) {
        setShouldAutoscrollJourneyAtFirst(false);
        clearInterval(autoscrollIntervalRef.current);
        autoscrollIntervalRef.current = undefined;
      }
    };

    const interval = SLIDE_SPEED_IN_MS + CARD_ANIMATION_SPEED_IN_MS;

    autoscrollIntervalRef.current = setInterval(handler, interval);

    return () => {
      if (autoscrollIntervalRef.current) {
        clearInterval(autoscrollIntervalRef.current);
        autoscrollIntervalRef.current = undefined;
      }
    };
  }, [shouldAutoscrollJourneyAtFirst, setShouldAutoscrollJourneyAtFirst, OnNextPressCallback]);

  const SliderSettings = React.useMemo(() => {
    const sliderSettings: Settings = {
      infinite: false,
      arrows: false,
      dots: false,
      slidesToShow: 3,
      slidesToScroll: 1,
      centerMode: false,
      initialSlide: initialPageIndex,
      speed: SLIDE_SPEED_IN_MS,
      afterChange: afterScroll,
      onSwipe: OnSwipeCallback,
      responsive: [
        {
          /** TILL BREAKPOINT-LG  **/
          breakpoint: Breakpoint.LG,
          settings: {
            slidesToShow: 1,
            centerMode: true,
            centerPadding: '56px',
          },
        },
        {
          /** TILL BREAKPOINT-XS  **/
          breakpoint: Breakpoint.XXS,
          settings: {
            slidesToShow: 1,
            centerMode: true,
            centerPadding: '28px',
          },
        },
      ],
    };

    return sliderSettings;
  }, [initialPageIndex, afterScroll, OnSwipeCallback]);

  const SliderRefCallback = React.useCallback((ref: Slider | null) => {
    if (ref === null) {
      sliderRef.current = undefined;
    }

    sliderRef.current = ref as Slider;
  }, []);

  const RenderContentCardCallback = React.useCallback(
    (type: ContentCardType, index: number) => {
      const isInFocus = isMobile ? index === currentIndex : index === currentIndex + 1;

      switch (type) {
        case 'scripting':
          return (
            <div key={`${type}-${index}`} className={classNames.itemWrapper}>
              <ScriptingContentCard isInFocus={isInFocus} />
            </div>
          );
        case 'process_automation':
          return (
            <div key={`${type}-${index}`} className={classNames.itemWrapper}>
              <ProcessAutomationContentCard isInFocus={isInFocus} />
            </div>
          );
        case 'machine_learning':
          return (
            <div key={`${type}-${index}`} className={classNames.itemWrapper}>
              <MachineLearningContentCard isInFocus={isInFocus} />
            </div>
          );
        case 'machine_reasoning':
          return (
            <div key={`${type}-${index}`} className={classNames.itemWrapper}>
              <MachineReasoningContentCard isInFocus={isInFocus} />
            </div>
          );
        case 'zero_touch':
          return (
            <div key={`${type}-${index}`} className={classNames.itemWrapper}>
              <ZeroTouchContentCard isInFocus={isInFocus} />
            </div>
          );
        case 'empty':
          return <div key={`${type}-${index}`} className={classNames.itemWrapper} />;
        default:
          throw new Error('Unknown content card type!');
      }
    },
    [currentIndex, isMobile]
  );

  const SliderElement = React.useMemo(() => {
    return (
      <Slider ref={SliderRefCallback} {...SliderSettings} className={classNames.slider}>
        {contentCards.map(RenderContentCardCallback)}
      </Slider>
    );
  }, [SliderSettings, contentCards, SliderRefCallback, RenderContentCardCallback]);

  const PrevButtonElement = React.useMemo(() => {
    const isDisabled = currentIndex === 0;

    return (
      <div className={StyleUtil.mergeClassNames([classNames.buttonWrapper, classNames.prev])}>
        <CircleButton arrowDirection={'left'} isDisabled={isDisabled} onPress={OnPrevPressCallback} />
      </div>
    );
  }, [currentIndex, OnPrevPressCallback]);

  const NextButtonElement = React.useMemo(() => {
    const isDisabled = currentIndex === contentCards.length - 3;

    return (
      <div className={StyleUtil.mergeClassNames([classNames.buttonWrapper, classNames.next])}>
        <CircleButton arrowDirection={'right'} isDisabled={isDisabled} onPress={OnNextPressCallback} />
      </div>
    );
  }, [contentCards, currentIndex, OnNextPressCallback]);

  const ShadeLeftElement = React.useMemo(() => {
    return <div className={StyleUtil.mergeClassNames([classNames.shade, classNames.left])} />;
  }, []);

  const ShadeRightElement = React.useMemo(() => {
    return <div className={StyleUtil.mergeClassNames([classNames.shade, classNames.right])} />;
  }, []);

  return (
    <div className={classNames.container}>
      {SliderElement}
      {PrevButtonElement}
      {NextButtonElement}
      {ShadeLeftElement}
      {ShadeRightElement}
    </div>
  );
});
